import React, { useCallback, useEffect, useMemo, useState } from 'react';

/* Imports from state package */
import { useOpenInternalLink } from '@cube3/state/src/Hoocs/Routing/withOpenInternalLink';

/* Imports components UI */
import { useAuth } from '@cube3/state/src/redux/components/Hooks/useAuth';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';
import { createConsentSubscriber } from '@cube3/state/src/redux/middleware/cookie-consent-middleware';
import AccountFlyoutContent from '@cube3/ui/src/appBar/AccountFlyoutContent';
import RoundedButton from '@cube3/ui/src/Buttons/RoundedButton';
import { LinearProgress } from '@material-ui/core';
import { useHistory } from 'react-router';
import { useLogout } from '../AccountPages/hooks/useLogout';
import { Avatar } from '@cube3/cubicle/src/core/atoms/Avatar/Avatar';
import useOutsideClick from '@cube3/cubicle/src/helpers/hooks/UseOutsideClick';
import { ContextMenu } from '@cube3/cubicle/src/core/molecules/ContextMenu';
import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import { useMenuToggle } from '@cube3/cubicle/src/helpers/hooks/useMenuToggle';
import { AccountIndicator } from '@cube3/cubicle/src/core/atoms/AccountIndicator';
import { isMobile } from 'react-device-detect';
import { MobileSlideSurface } from '@cube3/cubicle/src/components/Surface/MobileSlideSurface';
import { useModalActions } from '@cube3/state/src/redux/Hooks/useModalActions';

/**
 * @summary : Displays the current login status of the account. When not logged in, show a login button
 * @author Simon & ??
 */

interface Properties {
  sharelink?: boolean;
  //display the simpler version of the account indicator
  justAvatar?: boolean;
}

const AccountStatusIndicator = (props: Properties) => {
  const { sharelink, justAvatar = false } = props;

  const { openLink } = useOpenInternalLink();
  const { toggle, close, setAnchor, isMenuOpen, anchorEl } = useMenuToggle();

  const [canRemember, setCanRemember] = useState<boolean>();
  useEffect(() => {
    const unsub = createConsentSubscriber(['functional'])(
      () => setCanRemember(true),
      () => setCanRemember(false)
    );

    return () => {
      unsub();
    };
  }, []);

  const history = useHistory();

  const auth = useAuth({ setPath: history.push });

  const role = useResource__ALPHA({
    resourceType: 'role',
    resourceId: auth.currentAccount?.relationships.role.id,
    strategy: 'cache-only'
  });

  const { logout } = useLogout();
  const { closeAllModals } = useModalActions();

  const userInfo = useMemo(() => {
    return auth?.user
      ? {
          full_name: auth.user.full_name,
          email_address: auth.user.email_address,
          profile_picture: auth.user.profile_picture
            ? auth.user.profile_picture + '?updated_at=' + auth.user.updated_at
            : undefined
        }
      : {};
  }, [sharelink, auth.user]);

  const onClickSignOut = useCallback(() => {
    logout();
    toggle();
  }, [toggle, logout]);

  const onClickSignIn = useCallback(() => openLink('/account/login/'), []);
  const goToAccountSettings = useCallback(() => {
    openLink('/account/settings/');
    closeAllModals();
  }, []);

  const rootRef = useOutsideClick(close);

  if (!auth.user) {
    return sharelink ? null : (
      <Button
        label={'Sign in'}
        onClick={() => openLink('/account/login/')}
        buttonStyle="ghost"
        background="primary"
      />
    );
  }

  return (
    <div ref={rootRef}>
      {justAvatar ? (
        <RoundedButton
          onClick={toggle}
          extraButtonProperties={{ buttonRef: setAnchor }}
          data-cy="userAvatar"
          disableFocus={true}
        >
          <Avatar
            userInitial={
              userInfo.full_name === undefined
                ? userInfo.email_address
                : userInfo.full_name
            }
            imageUrl={userInfo.profile_picture}
          />
        </RoundedButton>
      ) : (
        <AccountIndicator
          onClick={toggle}
          data-cy="userAvatar"
          extraButtonProperties={{ buttonRef: setAnchor }}
          userInitial={
            userInfo.full_name === undefined
              ? userInfo.email_address
              : userInfo.full_name
          }
          imageUrl={userInfo.profile_picture}
          username={
            userInfo.full_name === undefined
              ? userInfo.email_address
              : userInfo.full_name
          }
          email={userInfo.email_address}
          showmore={isMenuOpen}
        />
      )}

      {isMenuOpen && (
        <>
          {isMobile ? (
            <MobileSlideSurface onClose={close} closeOnClick={true}>
              {(auth.loading || !auth.status) && !(sharelink && auth.user) ? (
                <LinearProgress variant="indeterminate" />
              ) : (
                <AccountFlyoutContent
                  account={auth.currentAccount}
                  user={userInfo}
                  role={role.resource?.display_name}
                  onClickSignIn={onClickSignIn}
                  onClickSignOut={onClickSignOut}
                  multipleAccounts={
                    auth.userAccounts && auth.userAccounts.length > 1
                  }
                  remember={auth.remember}
                  onClickRemember={auth.toggleRemember}
                  onClickSwitchAccounts={
                    sharelink ? undefined : auth.switchAccounts
                  }
                  canRemember={sharelink ? false : canRemember}
                  showEmail={sharelink}
                  onClickAccountSettings={
                    sharelink ? undefined : goToAccountSettings
                  }
                />
              )}{' '}
            </MobileSlideSurface>
          ) : (
            <ContextMenu
              anchor={anchorEl}
              placement={justAvatar ? 'bottom-start' : 'right-start'}
              scrollbarHeight={500}
            >
              {(auth.loading || !auth.status) && !(sharelink && auth.user) ? (
                <LinearProgress variant="indeterminate" />
              ) : (
                <AccountFlyoutContent
                  account={auth.currentAccount}
                  user={userInfo}
                  role={role.resource?.display_name}
                  onClickSignIn={onClickSignIn}
                  onClickSignOut={onClickSignOut}
                  // onClickSignUp={() => openLink('/account/sign-up/')}
                  multipleAccounts={
                    auth.userAccounts && auth.userAccounts.length > 1
                  }
                  remember={auth.remember}
                  onClickRemember={auth.toggleRemember}
                  onClickSwitchAccounts={
                    sharelink ? undefined : auth.switchAccounts
                  }
                  canRemember={sharelink ? false : canRemember}
                  showEmail={sharelink}
                  onClickAccountSettings={
                    sharelink ? undefined : goToAccountSettings
                  }
                />
              )}
            </ContextMenu>
          )}
        </>
      )}
    </div>
  );
};

export default AccountStatusIndicator;
